/**
 * Rogue Alloy van picker (design spec section 3, work package VANS).
 *
 * The picker itself (sections/rogue-van-picker.liquid) is server-rendered
 * Liquid: every make/model/generation link exists in the HTML with no
 * JavaScript at all. This module only adds the click handler that stores
 * the chosen van before the browser follows the link (3.4), the "My van
 * isn't listed" submit, and the live search/chip filter (3.7).
 *
 * Pure, DOM-free functions live at the top (unit tested by
 * tests/rogue-vans.test.js); the custom element that wires them to the DOM
 * is defined below and only installed when a `window`/`document` is
 * present, the same guard assets/rogue-fitment.js uses, so this file can be
 * imported under Node.
 *
 * assets/rogue-fitment.js owns the vehicle store, the vehicle-changed event
 * and the unlisted-vehicle record shape - this file imports those rather
 * than redefining them (see the work package's shared contract: "VANS,
 * SHOP and PAGES import from it and never edit it"). The import-map alias
 * resolves to the versioned Shopify asset URL; a relative import loses
 * that version and can load an older cached fitment module. Node's local
 * package imports map uses the same alias for the pure-function tests.
 */

import {
  createVehicleStore,
  findVehicleBySlug,
  ROGUE_VEHICLE_CHANGE_EVENT,
  syncVehicleCartAttributes,
  buildUnlistedVehicle,
} from '#rogue/fitment';

/**
 * Validates a `?return=` path (3.4.2): a same-origin path, nothing else.
 * Rejects anything that isn't a single leading slash - a full URL
 * ("https://example.com"), a protocol-relative one ("//example.com"), or a
 * backslash variant a browser would still treat as protocol-relative
 * ("/\example.com") - so the picker can never be used to redirect a
 * shopper off-site.
 * @param {unknown} raw
 * @returns {string|null} the path, unchanged, or null when it is not a safe same-origin path
 */
export function sanitizeReturnPath(raw) {
  if (typeof raw !== 'string' || raw === '') return null;
  if (raw[0] !== '/') return null;
  // "//example.com" and "/\example.com" (and "/\/example.com") are both
  // browser shorthand for a protocol-relative URL - reject the second
  // character being "/" or "\" in addition to the plain "//" check above.
  const second = raw[1];
  if (second === '/' || second === '\\') return null;
  // A tab or newline (?return=/%09/evil.example) is dropped by the browser's
  // URL parser, which then reads "//evil.example" (31 Oct 2026 security
  // check). Same rule as vehicleReturnPath in rogue-fitment.js.
  if (/[\\\u0000-\u0020]/.test(raw)) return null;
  return raw;
}

/**
 * Match search words in any order, accepting common make aliases. An
 * empty or whitespace-only query matches everything, so
 * clearing the field restores the full list.
 * @param {unknown} haystack
 * @param {unknown} query
 * @returns {boolean}
 */
export function matchesVanQuery(haystack, query) {
  const normalise = (value) => (typeof value === 'string' ? value : '')
    .normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    .replace(/\bvw\b/g, 'volkswagen').replace(/\bmerc\b/g, 'mercedes')
    .replace(/[^a-z0-9.]+/g, ' ').trim();
  const needle = normalise(query);
  if (needle === '') return true;
  const words = normalise(haystack).split(/\s+/);
  return needle.split(/\s+/).every((token) => words.some((word) => word.startsWith(token)));
}

/**
 * The aria-live count text under the search field (3.2: `"[n] vans"`).
 * @param {number} count
 * @returns {string}
 */
export function formatVanCount(count) {
  const n = Number.isFinite(count) && count > 0 ? Math.trunc(count) : 0;
  return `${n} van${n === 1 ? '' : 's'}`;
}

/**
 * Whether a tile/row's `data-body-type` attribute matches the active chip.
 * The "All vans" chip carries an empty value and matches everything,
 * including rows with no body type at all (3.2: the chip row only renders
 * once at least one van has `body_type`, but individual vans without one
 * still need to show up under "All vans").
 * @param {unknown} rowBodyType
 * @param {unknown} activeChipValue
 * @returns {boolean}
 */
export function matchesBodyTypeChip(rowBodyType, activeChipValue) {
  const chip = typeof activeChipValue === 'string' ? activeChipValue : '';
  if (chip === '') return true;
  if (chip === 'pickup') return rowBodyType === 'pickup' || rowBodyType === '4x4';
  return rowBodyType === chip;
}

/**
 * "Most popular" row (25 Oct 2026): it repeats vehicles that also sit in
 * their make group, so while a search or a type chip is active it is
 * hidden rather than filtered - the A to Z results alone answer the query,
 * with no duplicate tiles and no double count.
 * @param {unknown} query - the search box value
 * @param {unknown} activeChip - the active chip's `data-body-type` ('' for All vehicles)
 * @returns {boolean} true when the popular row should show
 */
export function showPopularRow(query, activeChip) {
  const q = typeof query === 'string' ? query.trim() : '';
  const chip = typeof activeChip === 'string' ? activeChip : '';
  return q === '' && chip === '';
}

/**
 * Deep links into the picker from the header's Vehicles menu (24 Oct 2026,
 * content edits row 9): `/pages/vehicles#trucks` opens the page with the
 * "Trucks and 4x4" chip already on. Only these fragments select a chip;
 * any other fragment (or none) leaves "All vehicles" on, so an ordinary
 * in-page anchor never filters the grid by accident.
 * @param {unknown} hash - `location.hash`, e.g. "#trucks"
 * @returns {string} the chip's `data-body-type` value, or '' for "All vehicles"
 */
export function chipFromHash(hash) {
  const key = (typeof hash === 'string' ? hash : '').replace(/^#/, '').trim().toLowerCase();
  const map = {
    trucks: 'pickup',
    pickups: 'pickup',
    '4x4': 'pickup',
    'small-vans': 'small_van',
    'medium-vans': 'medium_van',
    'large-vans': 'large_van',
  };
  return Object.prototype.hasOwnProperty.call(map, key) ? map[key] : '';
}

/* =========================================================================
 * DOM wiring, guarded so this module can be imported under Node (for
 * `node --test`) without a `window`/`document` present.
 * ======================================================================= */

if (typeof window !== 'undefined' && typeof document !== 'undefined' && 'customElements' in window) {
  installDomBehaviour();
}

function installDomBehaviour() {
  class RogueVanPicker extends HTMLElement {
    connectedCallback() {
      this.store = createVehicleStore({
        storage: safeLocalStorage(),
        cookies: {
          read: () => document.cookie,
          write: (cookieString) => {
            document.cookie = cookieString;
          },
        },
      });

      this.list = this.querySelector('[ref="list"]');
      this.searchWrap = this.querySelector('[ref="searchWrap"]');
      this.searchInput = this.querySelector('[ref="searchInput"]');
      this.searchCount = this.querySelector('[ref="searchCount"]');
      this.chipsWrap = this.querySelector('[ref="chipsWrap"]');
      this.emptyState = this.querySelector('[ref="emptyState"]');
      this.unlistedInput = this.querySelector('[ref="unlistedInput"]');
      this.unlistedForm = this.querySelector('[ref="unlistedForm"]');

      this.activeChip = '';

      this.markStoredVehicle();
      this.addEventListener('click', (event) => this.handleClick(event));

      if (this.unlistedForm) {
        this.unlistedForm.addEventListener('submit', (event) => {
          event.preventDefault();
          this.confirmUnlisted();
        });
      }

      // Search and chips need JavaScript to do anything useful (3.7: "With
      // JavaScript off, links work and search and chips are hidden") - both
      // start `hidden` in the markup and are only revealed once wired up.
      if (this.searchInput) {
        this.searchWrap?.removeAttribute('hidden');
        this.searchInput.addEventListener('input', () => this.applyFilters());
      }
      if (this.chipsWrap) {
        this.chipsWrap.removeAttribute('hidden');
        for (const chip of this.chipsWrap.querySelectorAll('[data-rogue-van-chip]')) {
          chip.setAttribute('aria-pressed', String((chip.getAttribute('data-body-type') || '') === this.activeChip));
        }
        // Menu deep links (#trucks): on load, and again when a header link
        // changes only the fragment on this same page (no reload happens).
        this.applyHashChip();
        window.addEventListener('hashchange', () => {
          this.applyHashChip();
          if (chipFromHash(window.location.hash) !== '') this.revealAfterMenuLink();
        });
      }

      this.applyFilters();
    }

    /**
     * Vehicles > Trucks tapped in the mobile drawer while already on this
     * page (golden check P1-6, 25 Oct 2026): the hash change filters the
     * grid, but the drawer stayed open over it, so the tap looked dead.
     * Close the drawer, then bring the chips and results into view once
     * its scroll lock has lifted.
     */
    revealAfterMenuLink() {
      const drawer = /** @type {any} */ (document.querySelector('header-drawer'));
      if (drawer && drawer.isOpen && typeof drawer.close === 'function') drawer.close();
      // The #trucks anchor carries the sticky-header scroll margin (rogue-vans.css).
      const target = this.querySelector('.rogue-vans__anchor') || this.chipsWrap || this.list;
      if (!target) return;
      const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.setTimeout(() => target.scrollIntoView({ block: 'start', behavior: smooth ? 'smooth' : 'auto' }), 350);
    }

    applyHashChip() {
      const wanted = chipFromHash(window.location.hash);
      if (wanted === '' && this.activeChip === '') return;
      const chip = [...this.chipsWrap.querySelectorAll('[data-rogue-van-chip]')]
        .find((candidate) => (candidate.getAttribute('data-body-type') || '') === wanted);
      if (!chip) return;
      this.selectChip(chip);
      // On mobile the chip row scrolls sideways: bring the chosen chip into
      // view without moving the page vertically.
      if (this.chipsWrap.scrollWidth > this.chipsWrap.clientWidth) {
        this.chipsWrap.scrollLeft += chip.getBoundingClientRect().left - this.chipsWrap.getBoundingClientRect().left - 16;
      }
    }

    /** @returns {Array<Record<string, unknown>>} the shared vehicle list rendered by snippets/rogue-vehicle-data.liquid */
    readVehicles() {
      const script = document.querySelector('script[data-rogue-vehicles]');
      if (!script || !script.textContent) return [];
      try {
        const parsed = JSON.parse(script.textContent);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }

    /**
     * Marks the stored vehicle (3.3): ink outline + "Your vehicle" chip in place
     * of the silhouette. Review finding (Minor): a multi-generation tile
     * (e.g. Transporter, with T2 through T7 as separate rows) must mark only
     * the one stored generation ROW, not the whole model tile - the earlier
     * version always added `is-stored` to the tile itself via `.closest()`,
     * which reads "Your vehicle" against every one of that model's generations.
     * `matchingLink` and its owning tile are the same element only for the
     * single-link tiles ("All years", and the compact 404 rows, both of
     * which render one `<a>` carrying `data-rogue-van-tile`,
     * `data-rogue-van-row` and `data-rogue-van-link` together) - there,
     * `.closest('[data-rogue-van-tile]')` returns the link itself (it checks
     * the element before its ancestors), so the tile-level treatment is
     * still correct because the tile IS the stored generation. For a
     * multi-generation tile the row lives inside a separate ancestor
     * `<div data-rogue-van-tile>`, so `tile !== matchingLink` there and only
     * the row gets `is-stored`.
     */
    markStoredVehicle() {
      const stored = this.store.get();
      if (!stored || !stored.slug) return;
      // Every match, not just the first: the "Most popular" row repeats a
      // vehicle that also sits in its make group, and both show the state.
      for (const matchingLink of this.querySelectorAll(`[data-rogue-van-link][data-slug="${cssEscape(stored.slug)}"]`)) {
        matchingLink.classList.add('is-stored');
        const tile = matchingLink.closest('[data-rogue-van-tile]');
        if (tile === matchingLink) tile.classList.add('is-stored');
      }
    }

    /** @returns {string|null} a validated `?return=` path from this page's own URL, or null */
    returnPath() {
      const params = new URLSearchParams(window.location.search);
      return sanitizeReturnPath(params.get('return'));
    }

    async handleClick(event) {
      const link = event.target.closest('[data-rogue-van-link]');
      if (link) {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button > 0) return;
        event.preventDefault();
        if (this._savingVehicle) return;
        this._savingVehicle = true;
        const saved = await this.storeAndAnnounce(findVehicleBySlug(this.readVehicles(), link.getAttribute('data-slug')));
        this._savingVehicle = false;
        if (saved) window.location.href = this.returnPath() || link.href;
        else this.showSaveError();
        return;
      }

      const chip = event.target.closest('[data-rogue-van-chip]');
      if (chip) this.selectChip(chip);
    }

    /** @param {Record<string, unknown>|null} vehicle */
    storeAndAnnounce(vehicle) {
      if (!vehicle) return;
      const stored = this.store.set(vehicle);
      if (!stored) return;
      const saved = syncVehicleCartAttributes(stored);
      document.dispatchEvent(new CustomEvent(ROGUE_VEHICLE_CHANGE_EVENT, { detail: { vehicle: stored } }));
      return saved;
    }

    showSaveError() {
      let status = this.querySelector('[data-rogue-save-error]');
      if (!status) {
        status = document.createElement('p');
        status.setAttribute('data-rogue-save-error', '');
        status.setAttribute('role', 'alert');
        this.prepend(status);
      }
      status.textContent = 'Your vehicle could not be saved to the basket. Please try again.';
    }

    /** "My van isn't listed" (3.4): builds and stores buildUnlistedVehicle's record, then goes to `return` or "all-wheels". */
    async confirmUnlisted() {
      if (this._savingVehicle) return;
      const typed = this.unlistedInput?.value.trim() || '';
      if (typed === '') return;
      this._savingVehicle = true;
      const saved = await this.storeAndAnnounce(buildUnlistedVehicle(typed));
      this._savingVehicle = false;
      if (!saved) { this.showSaveError(); return; }
      const returnPath = this.returnPath();
      window.location.href = returnPath || '/collections/all-wheels';
    }

    selectChip(chip) {
      this.activeChip = chip.getAttribute('data-body-type') || '';
      for (const sibling of this.chipsWrap.querySelectorAll('[data-rogue-van-chip]')) {
        sibling.classList.toggle('is-on', sibling === chip);
        sibling.setAttribute('aria-pressed', String(sibling === chip));
      }
      this.applyFilters();
    }

    /**
     * Three levels to hide/show: a row (one generation), the tile it sits in
     * (one model - hidden once every one of its rows is), and the make
     * group (hidden once every one of its tiles is). The "All years" tile
     * (3.3) is a single `<a>` that is both its own tile and its own row, so
     * it is queried, and hidden, at both levels without double effect.
     */
    applyFilters() {
      const query = this.searchInput?.value || '';
      let visible = 0;
      this.list?.querySelector('[data-rogue-popular-group]')?.toggleAttribute('hidden', !showPopularRow(query, this.activeChip));

      for (const group of this.list?.querySelectorAll('[data-rogue-make-group]') || []) {
        let groupVisible = 0;
        for (const tile of group.querySelectorAll('[data-rogue-van-tile]')) {
          const rows = tile.matches('[data-rogue-van-row]') ? [tile] : tile.querySelectorAll('[data-rogue-van-row]');
          let tileVisible = 0;
          for (const row of rows) {
            const matchesText = matchesVanQuery(row.getAttribute('data-search-text'), query);
            const matchesChip = matchesBodyTypeChip(row.getAttribute('data-body-type') || '', this.activeChip);
            const show = matchesText && matchesChip;
            row.toggleAttribute('hidden', !show);
            if (show) tileVisible += 1;
          }
          tile.toggleAttribute('hidden', tileVisible === 0);
          groupVisible += tileVisible;
        }
        group.toggleAttribute('hidden', groupVisible === 0);
        visible += groupVisible;
      }

      if (this.searchCount) {
        this.searchCount.textContent = query.trim() === '' ? '' : formatVanCount(visible);
      }

      const noMatch = query.trim() !== '' && visible === 0;
      this.emptyState?.toggleAttribute('hidden', !noMatch);
      if (noMatch && this.emptyState) {
        const label = this.emptyState.querySelector('[ref="emptyQuery"]');
        if (label) label.textContent = query.trim();
      }
      if (noMatch && this.unlistedInput && this.unlistedInput.value.trim() === '') {
        this.unlistedInput.value = query.trim();
      }
    }
  }

  if (!customElements.get('rogue-van-picker')) {
    customElements.define('rogue-van-picker', RogueVanPicker);
  }
}

function safeLocalStorage() {
  try {
    const key = '__rogue_probe__';
    window.localStorage.setItem(key, '1');
    window.localStorage.removeItem(key);
    return window.localStorage;
  } catch {
    return null;
  }
}

/** `CSS.escape` with a fallback for the rare browser/test runner without it. */
function cssEscape(value) {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(value);
  return String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
}
